import React, { useMemo, useState } from 'react';
import Icon from '../shared/Icon';
import { SURFACE, TONE_BADGE } from '../../utils/statusColors';
import { rotuloDoTipo, campo } from '../../design';

/**
 * DOCUMENTOS DO PROCESSO — o que o portal exige anexar junto com a proposta.
 *
 * Substitui o "Checklist de Declarações", que misturava três mundos e mostrava
 * oito linhas onde havia uma exigência de verdade (auditoria de 25/08/2026):
 * seis eram modelos nossos (`isDefault`), não exigência de ninguém, e apareciam
 * até em ComprasNet, onde não existe upload no cadastro da proposta. Modelo
 * nosso agora vive só no painel "Declarações e termos…" do card de Gerar.
 *
 * Regras da tela, nesta ordem:
 *   1. só OBRIGATÓRIAS aparecem; as opcionais ficam atrás de um link (nas
 *      listas já capturadas do BLL/BNC, 29 dos 30 documentos vêm opcionais);
 *   2. o slot de PROPOSTA mostra a Proposta Financeira que o atende, em vez de
 *      um select de template — era ele que casava "Proposta em papel timbrado"
 *      com "Termo de Exequibilidade da Proposta";
 *   3. quem atende a linha é sempre nomeado, com o status do documento.
 *
 * Componente de FOLHA: só props, sem estado de dado nem chamada de API.
 */

export interface ChecklistRow {
  key: string;
  exigencia: string;
  fonte: 'ia_edital' | 'ia_habilitacao' | 'bll' | 'padrao' | 'documento' | string;
  obrigatorio: boolean;
  templateId: number | null;
  templateTitle: string | null;
  matchConfidence: number;
  generatedDocId: number | null;
  docStatus: string;
  override: { status?: string; nota?: string; templateId?: number } | null;
  slotProposta?: boolean;
  atendidaPor?: { docId: number; name: string; type: string; status: string } | null;
  pendenteAssinatura?: boolean;
}

export interface ChecklistData {
  rows: ChecklistRow[];
  resumo: {
    total: number;
    pendentesObrigatorias: number;
    geradas: number;
    assinadas: number;
    obrigatorias?: number;
    opcionais?: number;
    obrigatoriasAtendidas?: number;
    pendentesAssinatura?: number;
  };
  fontes: { temIa: boolean; temBll: boolean };
  portal?: string | null;
  exigeDocumentos?: boolean;
  listaIndisponivel?: boolean;
}

/** Status do documento → tom do guia (§4: nenhuma cor de status inline nova). */
const STATUS_UI: Record<string, { label: string; cls: string }> = {
  nao_gerada: { label: 'falta', cls: TONE_BADGE.neutral },
  gerada: { label: 'gerada', cls: TONE_BADGE.progress },
  aguardando_assinatura: { label: 'aguard. assinatura', cls: TONE_BADGE.info },
  assinada: { label: 'assinada', cls: TONE_BADGE.open },
  upload_anexado: { label: 'anexado', cls: TONE_BADGE.open },
  atendida_outro: { label: 'atendida (outro)', cls: TONE_BADGE.success },
  nao_aplicavel: { label: 'não se aplica', cls: `${TONE_BADGE.neutral} line-through` },
};

const PENDENTE = new Set(['nao_gerada']);

/**
 * Rótulo do template no select. Dois templates dividem o mesmo `title`
 * ("Termo de Exequibilidade da Proposta", #8 PABX em Nuvem e #9 PABX +
 * Comodato): pelo title puro o select mostrava duas opções indistinguíveis.
 * Quando há colisão, manda o `name`, que é o que os diferencia.
 */
function rotularTemplates(
  templates: Array<{ id: number; name: string; title?: string; isActive?: boolean }>,
): Array<{ id: number; label: string }> {
  const ativos = templates.filter((t) => t.isActive !== false);
  const porTitle = new Map<string, number>();
  for (const t of ativos) {
    const k = (t.title || t.name).trim();
    porTitle.set(k, (porTitle.get(k) || 0) + 1);
  }
  return ativos.map((t) => {
    const title = (t.title || t.name).trim();
    return { id: t.id, label: (porTitle.get(title) || 0) > 1 ? t.name : title };
  });
}

interface Props {
  checklist: ChecklistData | null;
  loading: boolean;
  templates: Array<{ id: number; name: string; title?: string; isActive?: boolean }>;
  onReload: () => void;
  onLinkTemplate: (row: ChecklistRow, templateId: number | null) => void;
  onToggleOverride: (row: ChecklistRow, status: 'atendida_outro' | 'nao_aplicavel') => void;
  onGenerate: (row: ChecklistRow) => void;
  onViewDoc: (generatedDocId: number) => void;
  /** Anexar arquivo ao slot. Ausente = card em modo leitura (estágio Documentos). */
  onUpload?: (row: ChecklistRow, file: File) => Promise<void> | void;
  /** Chave da linha cujo upload está em curso. */
  uploadingKey?: string | null;
}

export default function ProcessDocumentsCard({
  checklist, loading, templates, onReload, onLinkTemplate, onToggleOverride,
  onGenerate, onViewDoc, onUpload, uploadingKey,
}: Props) {
  const opcoesTemplate = useMemo(() => rotularTemplates(templates), [templates]);
  const [verOpcionais, setVerOpcionais] = useState(false);

  const rows = checklist?.rows ?? [];
  const obrigatorias = rows.filter((r) => r.obrigatorio);
  const opcionais = rows.filter((r) => !r.obrigatorio);
  const pendentes = obrigatorias.filter((r) => PENDENTE.has(r.docStatus)).length;
  const pendentesAssinatura = rows.filter((r) => r.pendenteAssinatura).length;
  const visiveis = verOpcionais ? [...obrigatorias, ...opcionais] : obrigatorias;

  const linha = (row: ChecklistRow) => {
    const st = STATUS_UI[row.docStatus] || STATUS_UI.nao_gerada;
    const pendente = row.obrigatorio && PENDENTE.has(row.docStatus);
    const atendida = row.atendidaPor;
    const podeAnexar = !!onUpload && PENDENTE.has(row.docStatus);
    const enviando = uploadingKey === row.key;

    return (
      <div
        key={row.key}
        className={`rounded-lg border px-3 py-2 flex items-start gap-3 flex-wrap ${
          pendente ? 'bg-amber-500/5 border-amber-500/30' : 'bg-slate-900/50 border-slate-700/50'
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="text-xs text-slate-200 leading-snug">{row.exigencia}</div>

          {/* Quem atende a linha, nomeado. A Proposta Financeira já contava no
              envio (a ponte preenche o slot) mas não aparecia em lugar nenhum:
              a tela dizia "0/2 anexados" com a proposta assinada no HUB. */}
          {atendida ? (
            <div className="text-[10px] text-slate-400 mt-0.5">
              {rotuloDoTipo(atendida.type)} do HUB:{' '}
              <button
                type="button"
                onClick={() => onViewDoc(atendida.docId)}
                className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
              >
                {atendida.name}
              </button>
            </div>
          ) : (
            <div className="text-[10px] text-slate-500 mt-0.5">
              {row.obrigatorio ? 'obrigatório no portal' : 'opcional no portal'}
            </div>
          )}

          {row.pendenteAssinatura && (
            <div className="text-[10px] text-amber-400 mt-0.5">
              Gerada mas não assinada — o portal exige assinada, e o envio só materializa documento assinado.
            </div>
          )}

          {/* Select de template SÓ onde faz sentido: slot de proposta é atendido
              pela Proposta Financeira, não por declaração. Sem templates na
              mão (estágio Envio), o select seria uma caixa vazia. */}
          {!row.slotProposta && !atendida && opcoesTemplate.length > 0 && (
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <select
                value={row.templateId ?? ''}
                onChange={(e) => onLinkTemplate(row, e.target.value ? parseInt(e.target.value, 10) : null)}
                className={`rounded px-1.5 py-1 text-[11px] outline-none max-w-full ${campo}`}
                title="Declaração do HUB que atende esta exigência (vínculo manual vence o automático)"
              >
                <option value="">— atender com uma declaração do HUB —</option>
                {opcoesTemplate.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              {row.templateId && (
                <button
                  type="button"
                  onClick={() => onGenerate(row)}
                  className="text-[10px] px-2 py-0.5 bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 rounded transition-colors"
                  title="Gerar e salvar o PDF desta declaração"
                >Gerar</button>
              )}
              {row.templateId && row.matchConfidence < 0.7 && !row.override?.templateId && (
                <span className="text-[9px] text-amber-500/80">match automático fraco — confira</span>
              )}
            </div>
          )}
        </div>

        <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${st.cls}`}>{st.label}</span>

        <div className="flex items-center gap-1 shrink-0">
          {podeAnexar && (
            <label className="px-2 py-1 text-[10px] bg-slate-700 hover:bg-slate-600 text-slate-200 rounded cursor-pointer transition-colors">
              {enviando ? 'Enviando…' : 'Anexar'}
              <input
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUpload!(row, f);
                  e.target.value = '';
                }}
              />
            </label>
          )}
          <button
            type="button"
            onClick={() => onToggleOverride(row, 'atendida_outro')}
            className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${row.override?.status === 'atendida_outro' ? 'bg-emerald-600/40 text-emerald-300' : 'text-slate-500 hover:text-emerald-400'}`}
            title="Marcar como atendida por outro documento (fora do Hub)"
          >✓</button>
          <button
            type="button"
            onClick={() => onToggleOverride(row, 'nao_aplicavel')}
            className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${row.override?.status === 'nao_aplicavel' ? 'bg-slate-600 text-slate-300' : 'text-slate-500 hover:text-slate-300'}`}
            title="Marcar como não aplicável a esta licitação"
          >—</button>
        </div>
      </div>
    );
  };

  return (
    <div className={`${SURFACE.card} p-4 space-y-3`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-200 font-medium">
          <Icon name="check" className="w-4 h-4 text-orange-400" />
          Documentos do processo
          {checklist?.portal && (
            <span className="text-slate-500 font-normal text-xs">{String(checklist.portal).toUpperCase()}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {pendentes > 0 && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${TONE_BADGE.warning}`}>
              {pendentes} obrigatório(s) sem arquivo
            </span>
          )}
          {pendentes === 0 && obrigatorias.length > 0 && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${TONE_BADGE.success}`}>
              {obrigatorias.length} obrigatório(s) atendido(s)
            </span>
          )}
          <button
            type="button"
            onClick={onReload}
            className="text-[10px] text-slate-400 hover:text-slate-200 flex items-center gap-1"
            title="Recarregar a lista do processo"
          >
            <Icon name="refresh" className="w-3 h-3" /> Atualizar
          </button>
        </div>
      </div>

      {loading && <div className="text-xs text-slate-500">Carregando…</div>}

      {!loading && checklist?.listaIndisponivel && (
        <div className="text-xs text-amber-300/90 bg-amber-500/10 rounded-lg px-3 py-2">
          O HUB ainda não captura a lista de documentos deste portal. Confira no próprio portal o que precisa
          ser anexado antes de enviar.
        </div>
      )}

      {!loading && !checklist?.listaIndisponivel && obrigatorias.length === 0 && (
        <div className="text-xs text-slate-400">
          O portal não marcou nenhum documento como obrigatório neste processo.
          {opcionais.length > 0 && ' Os opcionais estão abaixo.'}
        </div>
      )}

      {!loading && visiveis.length > 0 && <div className="space-y-1.5">{visiveis.map(linha)}</div>}

      {!loading && opcionais.length > 0 && (
        <button
          type="button"
          onClick={() => setVerOpcionais((v) => !v)}
          className="text-[11px] text-slate-400 hover:text-slate-200 transition-colors"
        >
          {verOpcionais ? 'Ocultar' : 'Ver'} os {opcionais.length} documento(s) opcional(is) do processo
        </button>
      )}

      {!loading && pendentesAssinatura > 0 && (
        <div className="text-[10px] text-amber-400/90">
          {pendentesAssinatura} documento(s) gerado(s) sem assinatura — assine antes de enviar, ou o portal fica sem o arquivo.
        </div>
      )}
    </div>
  );
}
